import React, {useEffect, useRef, useState} from 'react';
import {Animated, Linking, StyleSheet} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import LinearGradient from 'react-native-linear-gradient';
import {RFValue} from 'react-native-responsive-fontsize';
import remoteConfig from '@react-native-firebase/remote-config';
import {
  Arrow,
  ArrowRow,
  Card,
  CardWrapper,
  CardDescription,
  CardTitle,
  Container,
  Grid,
  IconWrapper,
  ImageWrapper,
  PulseRing,
  PulseWrapper,
} from './styles';
import {useTheme} from 'styled-components/native';
import {useIsFocused, useNavigation} from '@react-navigation/native';
import {useAuth} from '../../../../context/auth';
import {useGlobal} from '../../../../context/global';
import {useLocalization} from '../../../../context/localization';
import {api} from '../../../../services/api';
import analytics from '@react-native-firebase/analytics';
import {BuyAgainModal} from '../../../../components/shared/BuyAgain/BuyAgainModal';
import {verificarElegibilidade} from '../../../../services/creditoMova';
import {getNextHomeCardIndex} from '../../../../utils/homeCardRotation';

const ROTATING_SERVICES = [
  {
    title: 'Produtos automotivos',
    description: 'Tudo para o seu carro em um só lugar.',
    icon: 'car-wrench',
    tipo: 'PA',
  },
  {
    title: 'Gás e água',
    description: 'Peça seu gás e água mineral com desconto.',
    icon: 'gas-cylinder',
    tipo: 'G',
  },
  {
    title: 'Lavagem',
    description: 'Encontre lavagens com preço especial.',
    icon: 'car-wash',
    tipo: 'LA',
  },
  {
    title: 'Emplacamento',
    description: 'Resolva o emplacamento do seu veículo.',
    icon: 'card-account-details-outline',
    tipo: 'E',
  },
  {
    title: 'Vistoria',
    description: 'Agende a vistoria do seu veículo.',
    icon: 'clipboard-check-outline',
    tipo: 'V',
  },
  {
    title: 'Produtos Pet',
    description: 'Ofertas em produtos pro seu pet.',
    icon: 'paw',
    tipo: 'P',
  },
  {
    title: 'Outros serviços',
    description: 'Confira tudo que o app oferece pra você.',
    icon: 'plus',
    tipo: undefined,
  },
] as const;

export type ServiceItem = {
  key: string;
  title: string;
  description: string;
  textColor?: string;
  icon?: string;
  iconColor?: string;
  iconImage?: string;
  iconBg: string;
  cardBg?: string;
  cardBorder?: string;
  cardGradient?: string[];
  titleSize?: number;
  pulse?: boolean;
  pulseColor?: string;
  onPress?: () => void;
};

interface BuyAgainOrder {
  produto: string;
  valorAtualProduto: number;
  id_produto: {$oid: string};
  litros: number;
  qtdCupons: number;
  valorAtualCompra: number;
  estado: string;
  cidade: string;
  categoria: string;
  tipoProduto: string;
  unidadeMedida: string;
  regiao: string;
  imagemProduto: string;
}
type HomeServicesProps = {
  splitAt?: number;
  middleContent?: React.ReactNode;
  onVipClubPress: () => void;
};

const ICON_SIZE = 18;

function getPlaceholderItem(numero: string): ServiceItem {
  return {
    key: '__placeholder__',
    title: 'Fale Conosco',
    description: 'Tire suas dúvidas',
    icon: 'whatsapp',
    iconColor: '#FFF',
    iconBg: '#25D366',
    onPress: () =>
      Linking.openURL(
        `https://wa.me/${numero}?text=Oi!%20Preciso%20de%20ajuda`,
      ),
  };
}

function ServiceCard({item}: {item: ServiceItem}) {
  const theme = useTheme();
  const {
    key,
    title,
    description,
    icon,
    iconColor,
    iconImage,
    iconBg,
    cardBg,
    cardBorder,
    cardGradient,
    titleSize,
    pulse,
    pulseColor,
    textColor,
    onPress,
  } = item;

  const ring1 = useRef(new Animated.Value(0)).current;
  const ring2 = useRef(new Animated.Value(0)).current;
  const [showHalo, setShowHalo] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!pulse || !pulseColor) {
      return;
    }

    setShowHalo(true);
    ring1.setValue(0);
    ring2.setValue(0);

    const DURATION = 1800;
    const PAUSE = 600;

    const makeLoop = (anim: Animated.Value) =>
      Animated.loop(
        Animated.sequence([
          Animated.timing(anim, {
            toValue: 1,
            duration: DURATION,
            useNativeDriver: true,
          }),
          Animated.timing(anim, {
            toValue: 0,
            duration: 0,
            useNativeDriver: true,
          }),
          // Animated.delay usa useNativeDriver: false internamente (fixo no
          // react-native), o que cria um interaction handle a cada ciclo do
          // loop e bloqueia o InteractionManager enquanto o pulse estiver
          // ativo. Um timing "parado" em 0 com useNativeDriver: true produz
          // a mesma pausa visual sem esse efeito colateral.
          Animated.timing(anim, {
            toValue: 0,
            duration: PAUSE,
            useNativeDriver: true,
          }),
        ]),
      );

    const loop1 = makeLoop(ring1);
    const loop2 = makeLoop(ring2);

    loop1.start();

    timerRef.current = setTimeout(() => loop2.start(), 900);

    return () => {
      loop1.stop();
      loop2.stop();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      ring1.setValue(0);
      ring2.setValue(0);
    };
  }, [pulse, pulseColor, ring1, ring2]);

  const ringStyle = (anim: Animated.Value) => ({
    opacity: anim.interpolate({
      inputRange: [0, 0.06, 0.5, 1],
      outputRange: [0, 0.14, 0.06, 0],
    }),
    transform: [
      {scale: anim.interpolate({inputRange: [0, 1], outputRange: [1, 1.18]})},
    ],
  });

  let cardBackground = cardBg || theme.colors.background;

  return (
    <PulseWrapper key={key}>
      {showHalo && pulseColor && (
        <>
          <PulseRing
            pulseColor={pulseColor}
            pointerEvents="none"
            style={ringStyle(ring1)}
          />
          <PulseRing
            pulseColor={pulseColor}
            pointerEvents="none"
            style={ringStyle(ring2)}
          />
        </>
      )}
      <CardWrapper
        cardBg={cardGradient ? 'transparent' : cardBackground}
        cardBorder={cardBorder}>
        {cardGradient && (
          <LinearGradient
            colors={cardGradient}
            start={{x: 0, y: 0}}
            end={{x: 1, y: 1}}
            style={[StyleSheet.absoluteFillObject, {borderRadius: RFValue(16)}]}
          />
        )}
        <Card onPress={onPress} activeOpacity={0.8}>
          {iconImage ? (
            <ImageWrapper
              source={{uri: iconImage}}
              style={{
                width: RFValue(32),
                height: RFValue(32),
                borderRadius: RFValue(64),
              }}
              resizeMode="contain"
            />
          ) : (
            <IconWrapper bgColor={iconBg} style={{backgroundColor: iconBg}}>
              <MaterialCommunityIcons
                name={icon ?? 'help-circle'}
                size={RFValue(ICON_SIZE)}
                color={iconColor ?? '#FFF'}
              />
            </IconWrapper>
          )}

          <CardTitle
            style={{
              ...(titleSize ? {fontSize: RFValue(titleSize)} : {}),
              ...(textColor ? {color: textColor} : {}),
            }}>
            {title}
          </CardTitle>
          <CardDescription style={textColor ? {color: textColor} : undefined}>
            {description}
          </CardDescription>
          <ArrowRow>
            <Arrow style={textColor ? {color: textColor} : undefined}>→</Arrow>
          </ArrowRow>
        </Card>
      </CardWrapper>
    </PulseWrapper>
  );
}

function padToEven(items: ServiceItem[], numero: string): ServiceItem[] {
  return items.length % 2 !== 0
    ? [...items, getPlaceholderItem(numero)]
    : items;
}


export function HomeServices({
  splitAt = 2,
  middleContent,
  onVipClubPress,
}: HomeServicesProps) {
  const theme = useTheme();
  const navigate = useNavigation();
  const isFocused = useIsFocused();
  const {isLogged, setShowSign, setIsEmprestimoEligible} = useAuth();
  const {disabledFeatures} = useGlobal();
  const {locationIsActive} = useLocalization();

  const [orders, setOrders] = useState<BuyAgainOrder[]>([]);
  const [showBuyAgainModal, setShowBuyAgainModal] = useState(false);
  const [rotatingServiceIndex, setRotatingServiceIndex] = useState(0);

  const [numeroFaleConosco, setNumeroFaleConosco] = useState('');

  const isDisabled = (key: string) => disabledFeatures.includes(key);

  useEffect(() => {
    getNextHomeCardIndex().then(index => setRotatingServiceIndex(index - 1));
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function fetchOrders() {
      try {
        const response = await api.get('/listaUltimasCompras');
        if (response.data && isMounted) {
          setOrders(response.data);
        }
      } catch {}
    }

    if (isLogged) {
      fetchOrders();
    }

    return () => {
      isMounted = false;
    };
  }, [isFocused, isLogged]);

  useEffect(() => {
    if (!isLogged) {
      setIsEmprestimoEligible(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const {elegivel} = await verificarElegibilidade();
        if (!cancelled) {
          setIsEmprestimoEligible(elegivel);
        }
      } catch {
        if (!cancelled) {
          setIsEmprestimoEligible(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isFocused, isLogged]);

  useEffect(() => {
    remoteConfig()
      .setDefaults({Fale_Conosco_Home: ''})
      .then(() =>
        remoteConfig().setConfigSettings({minimumFetchIntervalMillis: 0}),
      )
      .then(() => remoteConfig().fetchAndActivate())
      .then(() => {
        setNumeroFaleConosco(
          remoteConfig().getValue('Fale_Conosco_Home').asString(),
        );
      })
      .catch(() => {});
  }, []);

  async function handleBuyAgainCheckout() {
    setShowBuyAgainModal(false);
    analytics().logEvent('checkout_item_compra_recente_home');
    navigate.navigate('Payment', {
      localidade: {
        cidade: orders[0].cidade,
        estado: orders[0].estado,
        rank: orders[0].categoria,
        region: orders[0].regiao,
        tipo: orders[0].tipoProduto,
      },
      pedidos: {
        fuelId: orders[0].id_produto.$oid,
        imageUrl: orders[0].imagemProduto,
        liters: orders[0].litros,
        name: orders[0].produto,
        quantity: orders[0].qtdCupons,
        unidade:
          orders[0].unidadeMedida === 'L' ? 'Litros' : orders[0].unidadeMedida,
        value: orders[0].valorAtualCompra,
      },
      gnv: false,
    });
  }

  const handleAbastecaPress = () => {
    analytics().logEvent('clica_card_abasteca_aqui_home');
    if (locationIsActive) {
      navigate.navigate('ListagemPostos', {isUserLocation: true, tipo: 'C'});
    } else {
      navigate.navigate('Localization', {tipo: 'C'});
    }
  };

  const handleMultasPress = () => {
    if (!isLogged) {
      return setShowSign(true);
    }
    navigate.navigate('VehicleDebits');
  };

  const handleEmprestimoPress = () => {
    if (!isLogged) {
      setShowSign(true);
      return;
    }
    navigate.navigate('Emprestimo');
  };

  const handleMeusCartoesPress = () => {
    if (!isLogged) {
      return setShowSign(true);
    }
    navigate.navigate('CardsWallet');
  };

  const handleRotatingServicePress = () => {
    const {tipo} = ROTATING_SERVICES[rotatingServiceIndex];
    if (!tipo) {
      navigate.navigate('Shopping');
      return;
    }
    if (locationIsActive) {
      navigate.navigate('ListagemPostos', {isUserLocation: true, tipo});
    } else {
      navigate.navigate('Localization', {tipo});
    }
  };

  const handleMapaPress = () => {
    navigate.navigate('StationsMap');
  };

  const handleVigilantePress = () => {
    navigate.navigate('Vigilante');
  };

  const handleFeedbackPress = () => {
    Linking.openURL('https://usebaratao.com.br/feedback-posto/');
  };

  const handleComoComprarPress = () => {
    analytics().logEvent('clica_card_como_comprar_home');
    navigate.navigate('Tutoriais');
  };

  const services: ServiceItem[] = [
    {
      key: 'abasteça',
      title: 'Abasteça aqui',
      description: 'Encontre o posto mais barato perto de você.',
      icon: 'gas-station',
      iconBg: theme.colors.primary,
      cardBg: theme.colors.home_services_fuel_bg,
      cardBorder: theme.colors.primary,
      onPress: handleAbastecaPress,
      pulse: true,
      pulseColor: theme.colors.home_services_fuel_pulse,
    },
    ...(isLogged && orders.length >= 1
      ? [
          {
            key: 'buyAgain',
            title: 'Compre rápido',
            description: `${orders[0].produto} · ${orders[0].cidade} · ${
              orders[0].litros
            } ${
              orders[0].unidadeMedida === 'L'
                ? 'Litros'
                : orders[0].unidadeMedida
            }`,
            iconImage: orders[0].imagemProduto,
            iconBg: orders[0].categoria === 'ouro' ? '#FFF5E6' : '#9E9E9E',
            cardBg: orders[0].categoria === 'ouro' ? '#FFF5E6' : '#9E9E9E',
            cardBorder: orders[0].categoria === 'ouro' ? '#ff9900' : '#9E9E9E',
            onPress: () => {
              analytics().logEvent('clica_item_compra_recente_home');
              setShowBuyAgainModal(true);
            },
            pulse: false,
            pulseColor: orders[0].categoria === 'ouro' ? '#fe9d0c' : '#616161',
          },
        ]
      : []),
    ...(!isDisabled('multas')
      ? [
          {
            key: 'multas',
            title: 'Multas e IPVA',
            description: 'Parcele os débitos do seu veículo em até 12x, em segundos.',
            icon: 'car',
            iconBg: '#E5484D',
            onPress: handleMultasPress,
          },
        ]
      : []),

    {
      key: 'emprestimo',
      title: 'Empréstimo',
      description: 'Simule, compare e escolha o crédito ideal.',
      icon: 'cash-multiple',
      iconBg: '#FFBE18',
      onPress: handleEmprestimoPress,
    },
    {
      key: 'rotatingService',
      title: ROTATING_SERVICES[rotatingServiceIndex].title,
      description: ROTATING_SERVICES[rotatingServiceIndex].description,
      icon: ROTATING_SERVICES[rotatingServiceIndex].icon,
      iconBg: '#2D9CDB',
      onPress: handleRotatingServicePress,
    },
    ...(!isDisabled('vantagens')
      ? [
          {
            key: 'vantagens',
            title: 'Vantagens',
            description: '+10 mil lojas para você aproveitar!',
            icon: 'crown',
            iconBg: theme.colors.home_services_vantagens_bg,
            iconColor: theme.colors.home_services_vantagens_icon,
            textColor: theme.colors.home_services_vantagens_text,
            cardGradient: theme.colors.home_services_vantagens_gradient,
            cardBorder: theme.colors.home_services_vantagens_border,
            onPress: onVipClubPress,
          },
        ]
      : []),

    {
      key: 'comoComprar',
      title: 'Como comprar?',
      description: 'Veja tutoriais e aprenda a usar o app.',
      icon: 'play-circle-outline',
      iconBg: '#5E4EC2',
      onPress: handleComoComprarPress,
    },
    {
      key: 'meusCartoes',
      title: 'Meus cartões',
      description: 'Gerencie seus cartões cadastrados.',
      icon: 'credit-card-multiple-outline',
      iconBg: theme.colors.black,
      onPress: handleMeusCartoesPress,
    },

    ...(!isDisabled('mapa')
      ? [
          {
            key: 'mapa',
            title: 'Mapa de Postos',
            description: 'Veja postos próximos no mapa.',
            icon: 'map-marker',
            iconBg: '#0050B3',
            onPress: handleMapaPress,
          },
        ]
      : []),
    {
      key: 'vigilante',
      title: 'Fique de olho',
      description: 'Reporte preço mais alto que o baratão.',
      icon: 'eye-outline',
      iconBg: '#04D186',
      onPress: handleVigilantePress,
    },
 {
    key: 'feedback',
    title: 'Sua opinião',
    description: 'Você foi bem atendido no posto?',
    icon: 'thumb-up-outline',
    iconBg: '#EC4899',
    onPress: handleFeedbackPress,
  },
  {
    key: 'faleConosco',
    title: 'Fale Conosco',
    description: 'Tire suas dúvidas',
    icon: 'whatsapp',
    iconColor: '#FFF',
    iconBg: '#25D366',
    onPress: () => {
      Linking.openURL(
        `https://wa.me/${numeroFaleConosco}?text=Oi!%20Preciso%20de%20ajuda`,
      );
    },
  },
];

  const buyAgainModal = orders.length >= 1 && (
    <BuyAgainModal
      data={orders[0]}
      showModal={showBuyAgainModal}
      onDismiss={() => setShowBuyAgainModal(false)}
      success={handleBuyAgainCheckout}
    />
  );

  if (middleContent !== undefined) {
    const before = services.slice(0, splitAt);
    const after = padToEven(services.slice(splitAt), numeroFaleConosco);

    return (
      <>
        <Container>
          <Grid>
            {before.map(item => (
              <ServiceCard key={item.key} item={item} />
            ))}
          </Grid>
          {middleContent}
          <Grid style={{marginTop: middleContent ? 0 : RFValue(12)}}>
            {after.map(item => (
              <ServiceCard key={item.key} item={item} />
            ))}
          </Grid>
        </Container>
        {buyAgainModal}
      </>
    );
  }

  return (
    <>
      <Container>
        <Grid>
          {padToEven(services, numeroFaleConosco).map(item => (
            <ServiceCard key={item.key} item={item} />
          ))}
        </Grid>
      </Container>
      {buyAgainModal}
    </>
  );
}
